import { getSupabase } from '../../supabaseClient';
import { classifyMatchUserToGroupPayload } from '../../utils/matchJoinOutcomes';

/** Idempotent going RSVP without group matching. */
export async function upsertGoingRsvpMutationFn({ eventId, userId }) {
  const supabase = getSupabase();
  const { error } = await supabase
    .from('rsvps')
    .upsert(
      { event_id: eventId, user_id: userId, status: 'going' },
      { onConflict: 'event_id,user_id' },
    );
  if (error) throw error;
  return { action: 'going_skip', eventId };
}

export async function matchUserToGroupMutationFn({ eventId, skipGroupIds = [] }) {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc('match_user_to_group', {
    p_event_id: eventId,
    p_skip_group_ids: skipGroupIds,
  });
  if (error) throw error;
  return {
    action: 'going_queue',
    eventId,
    classified: classifyMatchUserToGroupPayload(data),
    raw: data,
  };
}

export async function createPrivateGroupMutationFn({ eventId, groupName }) {
  const supabase = getSupabase();
  // The creator's name (migration 136); without one the old signature auto-names "Group N".
  const { data, error } = await supabase.rpc(
    'create_private_group',
    groupName ? { p_event_id: eventId, p_name: groupName } : { p_event_id: eventId },
  );
  if (error) throw error;
  return { action: 'going_create', eventId, outcome: data };
}

export async function upsertRsvpStatusMutationFn({ eventId, userId, status }) {
  const supabase = getSupabase();
  const { error } = await supabase
    .from('rsvps')
    .upsert({ event_id: eventId, user_id: userId, status }, { onConflict: 'event_id,user_id' });
  if (error) throw error;
  return { action: 'upsert', eventId, status };
}

/** Idempotent delete — safe to replay. */
export async function deleteRsvpMutationFn({ eventId, userId }) {
  const supabase = getSupabase();
  const { error: rsvpError } = await supabase
    .from('rsvps')
    .delete()
    .eq('event_id', eventId)
    .eq('user_id', userId);
  if (rsvpError) throw rsvpError;

  await supabase
    .from('group_matching_queue')
    .delete()
    .eq('event_id', eventId)
    .eq('user_id', userId);

  return { action: 'delete', eventId };
}

export async function rsvpWriteMutationFn(variables) {
  const { action } = variables;
  switch (action) {
    case 'going_skip':
      return upsertGoingRsvpMutationFn(variables);
    case 'going_queue':
      return matchUserToGroupMutationFn(variables);
    case 'going_create':
      return createPrivateGroupMutationFn(variables);
    case 'upsert':
      return upsertRsvpStatusMutationFn(variables);
    case 'delete':
      return deleteRsvpMutationFn(variables);
    default:
      throw new Error(`Unknown RSVP action: ${action}`);
  }
}
